import {Button} from '@/components/ui/button/Button'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {ChevronRightIcon} from '@/components/ui/icon/Icon'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'
import {Text} from '@/components/ui/text/Text'
import dayjs, {formatDate} from '@/dayjs'
import {useAgendaCategoryPatientAgeRange} from '@/features/agenda/hooks/useAgendaCategoryPatientAgeRange'
import {useAgendaCategoryTeam} from '@/features/agenda/hooks/useAgendaCategoryTeam'
import {useAgendaNavigation} from '@/features/agenda/hooks/useAgendaNavigation'
import {useAgendaSearchParams} from '@/features/agenda/hooks/useAgendaSearchParams'
import {useGetAvailabilities} from '@/features/agenda/queries/useGetUsersAvailabilities'
import {useTranslation} from '@/translations/i18n'
import {ObjectKeys} from '@/utilities/helpers'
import {useEffect, useState} from 'react'

export const AvailableDatesButton = () => {
    const {t} = useTranslation()
    const {
        params: {date, entity},
        navigateToDate
    } = useAgendaNavigation()

    const {team} = useAgendaCategoryTeam()
    const {users} = useAgendaCategoryPatientAgeRange()
    const {
        searchParams: {subjectIds},
        isTeamCategory,
        isPatientAgeRangeCategory
    } = useAgendaSearchParams()

    const subjects =
        subjectIds ||
        (isTeamCategory
            ? team.map(teamMember => teamMember.id)
            : isPatientAgeRangeCategory
              ? users.map(user => user.id)
              : [])

    const [slotIndex, setSlotIndex] = useState(0)
    const [slots, setSlots] = useState<string[]>([])

    // Get availabilities
    const {
        data: availabilities,
        isLoading,
        isError
    } = useGetAvailabilities(
        {
            therapists: entity == 'users' ? subjects ?? [] : [],
            startDate: formatDate(date),
            endDate: formatDate(dayjs(date).add(6, 'month').toDate())
        },
        {
            enabled: !!subjects?.length && entity == 'users'
        }
    )

    useEffect(() => {
        if (availabilities && ObjectKeys(availabilities).length) {
            const availableSlots = ObjectKeys(availabilities)
                .filter(key => availabilities[key].length > 0 && dayjs(key).isAfter(dayjs(date)))
                .map(key => key)
            setSlots(availableSlots)
            setSlotIndex(0)
        } else {
            setSlots([])
        }
    }, [availabilities])

    const hasNextSlot = slots.length > 0 && slotIndex < slots.length - 1

    const handleNextSlot = () => {
        setSlotIndex(prev => prev + 1)
        navigateToDate(dayjs(slots[slotIndex]).toDate())
    }

    return (
        <Flexbox direction="column" gap={1.5} fullWidth>
            <Text size="textSm" palette="neutral" shade="500">
                {subjects.length > 1 ? t('agenda:next_available_slot') : t('agenda:next_available_slot_for_consultant')}
            </Text>
            <Flexbox justify="space-between" align="center" fullWidth>
                {isLoading ? (
                    <Skeleton width={100} height={20} />
                ) : isError ? (
                    <Text size="textSm" palette="neutral" shade="500">
                        {t('errors:default')}
                    </Text>
                ) : slots.length > 0 && hasNextSlot ? (
                    <Button
                        variant="ghost"
                        shape="text"
                        size="sm"
                        fullWidth
                        onClick={handleNextSlot}
                        style={{width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}
                    >
                        <Text size="textSm">{dayjs(slots[slotIndex]).format('DD/MM/YYYY')}</Text>
                        <ChevronRightIcon />
                    </Button>
                ) : (
                    <Text size="textSm">{t('agenda:no_available_slots_next_6_months')}</Text>
                )}
            </Flexbox>
        </Flexbox>
    )
}
